<script setup lang="ts">
import { getInitials } from '@/utils/generalFunctions'
import {
  InfoCircleIcon,
  Settings01Icon,
  Trash03Icon,
  UserCircleIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIAvatar,
  UIButton,
  UIForm,
  UIFormItem,
  UIInput,
  UIInputGroup,
  UIRadio,
  UISelect,
  UITooltip,
} from '@gohighlevel/ghl-ui'
import { computed, h, reactive, ref, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { TYPE, useToast } from 'vue-toastification'
import { CalendarType, EventType, TeamMember } from '../../models/calendar'
import AppState, {
  fetchConnections,
  getNestedCustomValues,
  setEditCalendarValidationData,
} from '../../states/app'
import calendarScheduleState from '../../states/calendarScheduleState'
import {
  handleNestedCustomValueModification,
  meetingLocationFieldOptions,
} from '../../utils/customValueMapper'
import { getDefaultTeamMember, getTooltip } from '../../utils/generalFunctions'
import { hasDuplicateKeyValue, hasEmptyKeys } from '../../utils/validation'
import AdvancedCalendarScheduleSettingsModal from '../Settings/AdvancedCalendarScheduleSettingsModal.vue'
import Tooltip from '../common/Tooltip.vue'
import CustomVariablePickerV2 from '../dynamic/CustomVariablePickerV2.vue'
const formRef = ref()

const meetingLocationNew = ref({
  custom: '',
  inbound_call: '{{location.phone}}',
  outbound_call: '{{location.phone}}',
  physical: '{{location.full_address}}',
})

const { t } = useI18n()

const state = reactive({
  isLoading: false,
  zoomConnections: [] as any,
  teamMembers: [] as TeamMember[],
  formData: {},
  eventType: EventType.RoundRobin_OptimizeForAvailability,
  customMeetingLocation: {},
  showAdvancedSettings: false,
  msTeamsConnections: [] as any,
  isFirstLoad: false,
})

const toast = useToast()
const meetingLocationError = ref('')

const meetingLocationRef = ref(meetingLocationFieldOptions)

// Function to load and update custom values with nested folder structure
const loadMeetingLocationCustomValues = async () => {
  // Set base options first (synchronous)
  meetingLocationRef.value = meetingLocationFieldOptions

  // Then fetch and add custom values (asynchronous)
  const nestedCustomValues = await getNestedCustomValues()
  const customValues = handleNestedCustomValueModification(nestedCustomValues)

  if (customValues) {
    meetingLocationRef.value = [...meetingLocationFieldOptions, customValues]
  }
}

// Load custom values once when component mounts
watch(
  () => AppState.locationId,
  newLocationId => {
    if (newLocationId) {
      loadMeetingLocationCustomValues()
    }
  },
  { immediate: true }
)

const emit = defineEmits([
  'onTeamMemberSelect',
  'onAdvancedSchedulingSettingsUpdate',
])
const usersInLocationOptions = computed(() => {
  return (AppState.globalUsers as any)?.users.map((x: any) => {
    const isRecurringEnabled =
      AppState.editCalendarValidationData.enableRecurring
    const hasDateSpecificHours =
      calendarScheduleState.state.userSchedulesInfo[x.id]?.hasDateSpecificHours
    const isDisabled =
      (state.teamMembers.length === 1 &&
        state.teamMembers[0].locationConfigurations.length > 1) ||
      ((selectedCalendarType.value === CalendarType.CLASS_BOOKING ||
        selectedCalendarType.value === CalendarType.PERSONAL ||
        isRecurringEnabled) &&
        state.teamMembers.length === 1 &&
        !state.teamMembers.some(y => y.userId === x.id)) ||
      (isRecurringEnabled && hasDateSpecificHours)
    return {
      value: x.id,
      label: x.name,
      disabled: isDisabled,
      tooltip: getTooltipForAddingUser(isRecurringEnabled, x.id, x.name),
    }
  })
})

const getTooltipForAddingUser = (
  isRecurringEnabled: boolean,
  userId: string,
  userName: string
) => {
  if (isRecurringEnabled) {
    const info = calendarScheduleState.state.userSchedulesInfo[userId]
    if (info?.hasDateSpecificHours) {
      return t(
        'settings.availability.schedule.incompatible_schedule_with_recurring',
        { user: userName ? `${userName}'s` : '' }
      )
    }
  }
  return ''
}

const priorityOptions = [
  {
    label: t('calendar_advanced.meeting_details.high_priority'),
    value: 1,
  },
  {
    label: t('calendar_advanced.meeting_details.medium_priority'),
    value: 0.5,
  },
  {
    label: t('calendar_advanced.meeting_details.low_priority'),
    value: 0,
  },
]

const oldMeetingOptions = {
  zoom_conference: 'true',
  ms_teams_conference: 'true',
  custom: 'false',
  google_conference: 'meet',
  inbound_call: 'phone',
  physical: 'address',
}

const isAliasFieldEnabled = ref<string[]>([])

const meetingLocationOptions = computed(() => {
  const options = [
    {
      label: t('calendar_advanced.meeting_details.custom'),
      value: 'custom',
    },
    {
      label: t('calendar_advanced.meeting_location.zoom'),
      value: 'zoom_conference',
      disabled: selectedCalendarType.value === CalendarType.CLASS_BOOKING,
    },
    {
      label: t('calendar_advanced.meeting_location.google_meet'),
      value: 'google_conference',
      disabled: selectedCalendarType.value === CalendarType.CLASS_BOOKING,
    },
    {
      label: t('calendar_advanced.meeting_location.phone'),
      value: 'inbound_call',
      disabled: selectedCalendarType.value === CalendarType.CLASS_BOOKING,
    },
    {
      label: t('calendar_advanced.meeting_location.full_address'),
      value: 'physical',
      disabled: selectedCalendarType.value === CalendarType.CLASS_BOOKING,
    },
    {
      label: t('calendar_advanced.meeting_location.ask_the_booker'),
      value: 'booker',
      disabled:
        (selectedCalendarType.value !== CalendarType.ROUND_ROBIN &&
          selectedCalendarType.value !== CalendarType.PERSONAL) ||
        state.teamMembers.length > 1,
    },
    {
      label: 'Microsoft Teams',
      value: 'ms_teams_conference',
      disabled: selectedCalendarType.value === CalendarType.CLASS_BOOKING,
    },
  ]

  const enabledOptions = options.filter(option => !option.disabled)
  const disabledOptions = options.filter(option => option.disabled)

  if (disabledOptions.length > 0) {
    const isRoundRobin = selectedCalendarType.value === CalendarType.ROUND_ROBIN
    return [
      {
        type: 'group',
        label: `Supported Meeting Locations`,
        key: 'enabled-options',
        children: enabledOptions,
      },
      {
        type: 'group',
        label: isRoundRobin
          ? t(
              'calendar_advanced.meeting_details.not_supported_with_multiple_team_members'
            )
          : calendarTypeLabel.value,
        key: 'disabled-options',
        children: disabledOptions,
      },
    ]
  }
  return options
})

const calendarTypeLabel = computed(() => {
  const type = selectedCalendarType.value
  return type === 'event'
    ? t('calendar_advanced.meeting_details.not_supported_event')
    : type === 'class_booking'
    ? t('calendar_advanced.meeting_details.not_supported_class_booking')
    : type === 'collective'
    ? t('calendar_advanced.meeting_details.not_supported_collective_booking')
    : type === 'service_booking'
    ? t('calendar_advanced.meeting_details.not_supported_service_booking')
    : type === 'personal'
    ? t('calendar_advanced.meeting_details.not_supported_personal_booking')
    : t('calendar_advanced.meeting_details.not_supported_round_robin')
})

const editingCalendar = computed(() => {
  return AppState.editingCalendar
})

const selectedCalendarType = computed(() => {
  return editingCalendar.value?.calendarType
})

const teamMembersWithFullDetails = computed(() => {
  return state.teamMembers.map(x => {
    const user = (AppState.globalUsers as any).users.find(
      (y: any) => y.id === x.userId
    )
    return {
      ...x,
      ...user,
    }
  })
})

const methods = {
  async loadData() {
    // if first load flag is false, then set it to true and set the initial load flag to true
    if (!state.isFirstLoad) {
      state.isFirstLoad = true
      calendarScheduleState.state.initialLoad = true
    }
    const promises = [methods.loadConnection()]
    if (
      AppState.isScheduleEnabled &&
      (AppState.globalUsers as any)?.users?.length > 0 &&
      editingCalendar.value?.calendarType !== CalendarType.EVENT
    ) {
      promises.push(
        calendarScheduleState.fetchSchedulesForUsers(
          [],
          editingCalendar.value?.id
        )
      )
    }
    try {
      await Promise.all(promises)
    } finally {
      calendarScheduleState.state.initialLoad = false
    }

    state.teamMembers =
      editingCalendar.value?.teamMembers
        ?.filter(x => x.selected)
        ?.map(x => {
          let priority = Number(x.priority)
          if (isNaN(priority)) {
            priority = 0.5
          }
          return { ...x, priority }
        })
        ?.filter(x =>
          AppState.globalUsers?.users.map(y => y.id).includes(x.userId)
        ) || []
    state.eventType =
      editingCalendar.value?.eventType ||
      EventType.RoundRobin_OptimizeForAvailability

    AppState.editCalendarValidationData.enableSameUserAssignment =
      editingCalendar.value?.enableSameUserAssignment || false
  },
  priorityChanged(userId, value) {
    state.teamMembers = state.teamMembers.map(x => {
      if (x.userId !== userId) {
        return x
      }

      return {
        ...x,
        priority: value,
      }
    })
  },
  isPrimaryChanged(userId, value) {
    state.teamMembers = state.teamMembers.map(x => {
      if (x.userId !== userId) {
        return {
          ...x,
          isPrimary: !value,
        }
      }

      return {
        ...x,
        isPrimary: value,
      }
    })
  },

  meetingLocationTypeChangeNew(userId: string, value: string, index: number) {
    meetingLocationError.value = ''
    state.teamMembers = state.teamMembers.map(x => {
      if (x.userId !== userId) {
        return x
      }
      if (
        [
          'zoom_conference',
          'google_conference',
          'booker',
          'ms_teams_conference',
        ]?.indexOf(value) !== -1 &&
        x.locationConfigurations.find(y => y.kind === value)
      ) {
        toast(t('calendar_advanced.meeting_details.location_exists_toast'), {
          type: TYPE.ERROR,
        })
        return x
      }
      const zoomConnection = state.zoomConnections.find(connection => {
        const isUserAssociated =
          connection.users?.[userId] || connection.ownerId === userId
        const isLocationUserAssociated =
          !connection.locationUserAssociation ||
          (connection.locationUserAssociation[AppState.locationId] &&
            connection.locationUserAssociation[AppState.locationId].includes(
              userId
            ))
        return isUserAssociated && isLocationUserAssociated
      })

      if (!zoomConnection && value === 'zoom_conference') {
        toast(t('calendar_advanced.meeting_details.zoom_not_added'), {
          type: TYPE.ERROR,
        })
        return x
      }
      const msTeamsConnection = state.msTeamsConnections.find(connection => {
        const isUserAssociated =
          connection.users?.[userId] || connection.ownerId === userId
        const isLocationUserAssociated =
          !connection.locationUserAssociation ||
          (connection.locationUserAssociation[AppState.locationId] &&
            connection.locationUserAssociation[AppState.locationId].includes(
              userId
            ))
        return isUserAssociated && isLocationUserAssociated
      })

      if (!msTeamsConnection && value === 'ms_teams_conference') {
        toast(t('calendar_advanced.meeting_details.ms_teams_not_added'), {
          type: TYPE.ERROR,
        })
        return x
      }

      // Update isZoomAdded based on the new meeting location type
      // oldMeetingOptions maps: zoom_conference='true', google_conference='meet',
      // ms_teams_conference='true', inbound_call='phone', physical='address', custom='false'
      x.isZoomAdded = oldMeetingOptions[value] || 'false'

      // Handle Zoom conference
      if (value === 'zoom_conference') {
        x.zoomOauthId = zoomConnection?.oauthId || ''
        x.locationConfigurations[index].zoomOauthId =
          zoomConnection?.oauthId || ''
      } else {
        // Clear zoom OAuth ID when switching away from zoom
        x.locationConfigurations[index].zoomOauthId = undefined
      }

      // Handle MS Teams conference
      if (value === 'ms_teams_conference') {
        x.isMsTeamsAdded = 'true'
        x.msTeamsOauthId = msTeamsConnection?.oauthId || ''
        x.locationConfigurations[index].msTeamsOauthId =
          msTeamsConnection?.oauthId || ''
      } else {
        // Clear MS Teams OAuth ID when switching away from MS Teams
        x.isMsTeamsAdded = undefined
        x.locationConfigurations[index].msTeamsOauthId = undefined
      }

      x.locationConfigurations[index].kind = value

      if (
        ['physical', 'inbound_call', 'outbound_call', 'custom'].includes(value)
      ) {
        x.locationConfigurations[index].location =
          meetingLocationNew.value[value]
        x.meetingLocation = meetingLocationNew.value[value]
      } else if (
        [
          'zoom_conference',
          'google_conference',
          'ms_teams_conference',
          'booker',
        ].includes(value)
      ) {
        // Clear location for conference types as meeting links are auto-generated
        x.locationConfigurations[index].location = ''
        x.meetingLocation = ''
      }
      return x
    })
  },
  meetingLocationChanged(userId, value) {
    meetingLocationError.value = ''
    state.teamMembers = state.teamMembers.map(x => {
      if (x.userId !== userId) {
        return x
      }

      x.locationConfigurations[0].location = value

      return {
        ...x,
        meetingLocation: value,
      }
    })
  },
  emitAdvancedFormData(settings) {
    emit('onAdvancedSchedulingSettingsUpdate', settings)
  },
  // eslint-disable-next-line max-params
  meetingLocationChangedNew(
    userId: string,
    value: string,
    index: number,
    kind: string
  ) {
    meetingLocationError.value = ''
    state.teamMembers = state.teamMembers.map(x => {
      if (x.userId !== userId) {
        return x
      }
      x.locationConfigurations[index].location = value
      if (kind === 'custom') {
        meetingLocationNew.value.custom = value
      }

      return {
        ...x,
        meetingLocation: value,
      }
    })
  },
  meetingAliasChanged(userId: string, value: string, index: number) {
    state.teamMembers = state.teamMembers.map(x => {
      if (x.userId !== userId) {
        return x
      }

      x.locationConfigurations[index].alias = value

      return {
        ...x,
      }
    })
  },
  removeTeamMember(userId) {
    state.teamMembers = state.teamMembers.filter(x => x.userId !== userId)
    emit('onTeamMemberSelect', state.teamMembers)
  },

  // HERE 2
  teamMembersChanged(value) {
    meetingLocationError.value = ''
    let addedTeamMembers = []
    const existingTeamMemberIds = state.teamMembers.map(x => x.userId)

    const removedTeamMembers = existingTeamMemberIds.filter(
      x => !value.includes(x)
    )

    const isPrimary = state.teamMembers.some(
      x => x.isPrimary && removedTeamMembers.includes(x.userId)
    )

    if (selectedCalendarType.value === CalendarType.COLLECTIVE && isPrimary) {
      return
    }
    addedTeamMembers = value.filter(x => !existingTeamMemberIds.includes(x))

    state.teamMembers = state.teamMembers.filter(
      x => !removedTeamMembers.includes(x.userId)
    )
    state.teamMembers = [
      ...(state.teamMembers || []),
      ...(addedTeamMembers || []).map(x => {
        const teamMemberDetail = (AppState.globalUsers as any).users.find(
          obj => obj.id === x
        )
        return processTeamMemberInfo(teamMemberDetail, x)
      }),
    ]
    //}
    emit('onTeamMemberSelect', state.teamMembers)
  },
  async loadConnection() {
    await fetchConnections(AppState.locationId)
    const zoomConnections =
      AppState.connections?.locationConnections?.zoom || []
    const msTeamsConnections =
      AppState.connections?.locationConnections?.ms_teams || []

    state.msTeamsConnections = msTeamsConnections

    state.zoomConnections = zoomConnections
  },
  async getTeamMembers() {
    try {
      await formRef.value.getForm().validate()
      isAliasFieldEnabled.value = []
      if (
        (selectedCalendarType.value === CalendarType.ROUND_ROBIN ||
          selectedCalendarType.value === CalendarType.SERVICE ||
          selectedCalendarType.value === CalendarType.PERSONAL) &&
        state.teamMembers?.length === 1
      ) {
        if (hasEmptyKeys(state?.teamMembers[0]?.locationConfigurations)) {
          meetingLocationError.value = t('validation_messages.meeting_location_empty')
          throw new Error('Empty value for meeting location is not allowed')
        }
        if (hasDuplicateKeyValue(state.teamMembers[0].locationConfigurations)) {
          meetingLocationError.value = t('validation_messages.meeting_location_duplicate')
          throw new Error(
            'Duplicate value for same location type is not allowed'
          )
        }
      }

      if (
        state.teamMembers.length > 1 &&
        state.teamMembers.find(item =>
          item.locationConfigurations.find(x => x.kind === 'booker')
        )
      ) {
        meetingLocationError.value = t('validation_messages.meeting_location_booker_multiple')
        throw new Error(
          'Ask the Booker is not allowed for multiple team members'
        )
      }

      if (selectedCalendarType.value === CalendarType.CLASS_BOOKING) {
        if (!state.teamMembers?.[0]?.locationConfigurations?.[0]?.location) {
          meetingLocationError.value = t('validation_messages.meeting_location_required')
          throw new Error(t('validation_messages.meeting_location_required'))
        }
      }
    } catch (error) {
      return null
    }

    return state.teamMembers.map(x => {
      // selectedCalendarType.value === CalendarType.ROUND_ROBIN &&
      //   delete x.meetingLocation
      x?.locationConfigurations?.map((y, l) => {
        if (y?.kind !== 'custom' && y?.alias) {
          delete y.alias
        }
        if (y?.kind === 'booker' && y?.location) {
          y.location = ''
        }
        y.meetingId = y?.kind + '_' + l
      })
      return x
    })
  },
  getEventType() {
    return state.eventType
  },
  shouldAddressEnable(locationConfig: {
    kind: string
    location: string
    position: number
  }) {
    return (
      [
        'zoom_conference',
        'google_conference',
        'booker',
        'ms_teams_conference',
      ].indexOf(locationConfig.kind) === -1
    )
  },
  openAliasField(index: string) {
    isAliasFieldEnabled.value.push(index)
  },
  closeAliasField(index: string) {
    isAliasFieldEnabled.value = isAliasFieldEnabled.value.filter(
      x => x !== index
    )
  },
}

const rules = computed(() => {
  return {
    teamMembers: {
      validator() {
        if (selectedCalendarType.value === CalendarType.EVENT) {
          return true
        }

        if (
          selectedCalendarType.value === CalendarType.CLASS_BOOKING ||
          selectedCalendarType.value === CalendarType.PERSONAL
        ) {
          if (teamMembersWithFullDetails.value.length > 1) {
            return new Error(
              t(
                'calendar_advanced.meeting_details.only_one_team_member_allowed'
              )
            )
          }
        }

        if (selectedCalendarType.value === CalendarType.COLLECTIVE) {
          if (teamMembersWithFullDetails.value.length < 2) {
            return new Error(
              t(
                'calendar_advanced.meeting_details.at_least_two_team_members_required'
              )
            )
          }
        }

        if (teamMembersWithFullDetails.value.length === 0) {
          return new Error(
            t(
              'calendar_advanced.meeting_details.at_least_one_team_member_required'
            )
          )
        }
        return true
      },
      trigger: ['input', 'blur'],
    },
    meetingLocation: {
      validator() {
        if (selectedCalendarType.value !== CalendarType.CLASS_BOOKING) {
          meetingLocationError.value = ''
          return true
        }

        if (teamMembersWithFullDetails.value.length === 0) {
          meetingLocationError.value = ''
          return true
        }

        const meetingLocation =
          teamMembersWithFullDetails.value[0]?.meetingLocation

        if (!meetingLocation) {
          meetingLocationError.value = t('validation_messages.meeting_location_required')
          return new Error(t('validation_messages.meeting_location_required'))
        }

        meetingLocationError.value = ''
        return true
      },
      trigger: ['input', 'blur'],
    },
  }
})

watch(
  () => state.teamMembers,
  () => {
    setEditCalendarValidationData('teamMembers', state.teamMembers)

    if (state.teamMembers.length === 1) {
      state.teamMembers[0].isPrimary = true
    }
  },
  { immediate: true }
)

watch(
  () => AppState.editCalendarValidationData.removeMultipleLocation,
  () => {
    state.teamMembers = state.teamMembers.map(x => {
      return {
        ...x,
        locationConfigurations: x.locationConfigurations.slice(0, 1),
      }
    })
  }
)

watch(
  () => AppState.editCalendarValidationData.selectedTeamMember,
  () => {
    const selectedMemberValue =
      AppState.editCalendarValidationData.selectedTeamMember
    if (!selectedMemberValue) {
      return
    }
    const selectedMember = usersInLocationOptions.value.find(
      x => x.value === selectedMemberValue
    )
    if (!selectedMember) {
      return
    }
    const selectedMemberDetail = (AppState.globalUsers as any)?.users?.find(
      obj => obj.id === selectedMemberValue
    )
    if (selectedMemberDetail) {
      state.teamMembers = [
        processTeamMemberInfo(selectedMemberDetail, selectedMemberValue),
      ]
    }
  }
)

watch(
  () => state.eventType,
  () => {
    if (state.eventType === EventType.RoundRobin_OptimizeForEqualDistribution) {
      state.teamMembers = state.teamMembers.map(x => {
        return {
          ...x,
          priority: 0.5,
        }
      })
    }
  }
)

watch(
  () => editingCalendar.value,
  () => {
    methods.loadData()
  }
)

watchEffect(() => {
  AppState.isMultipleLocationEnabled =
    state.teamMembers?.length === 1 &&
    (selectedCalendarType?.value === CalendarType.ROUND_ROBIN ||
      selectedCalendarType?.value === CalendarType.PERSONAL) &&
    state.teamMembers?.[0]?.locationConfigurations.length > 1
})

defineExpose({
  methods,
})
const toggleAdvancedSettingsModal = () => {
  state.showAdvancedSettings = !state.showAdvancedSettings
}

function processTeamMemberInfo(teamMemberDetail, userId) {
  if (!teamMemberDetail) {
    return getDefaultTeamMember(userId)
  }

  const zoomConnection = state.zoomConnections.find(
    connection => connection.ownerId === userId
  )
  const msTeamsConnection = state.msTeamsConnections.find(
    connection => connection.ownerId === userId
  )

  const selectedCustomMeetingLocation =
    teamMemberDetail.locationWiseMeetingLocation &&
    teamMemberDetail.locationWiseMeetingLocation[AppState.locationId]
  const selectedIsZoomAdded =
    teamMemberDetail.locationWiseZoomAdded &&
    teamMemberDetail.locationWiseZoomAdded[AppState.locationId]

  // Determine the kind based on user's preferred meeting location type
  // selectedIsZoomAdded values: 'true' (zoom), 'meet' (google), 'ms_teams', 'phone', 'address', 'false'/'custom' (custom)
  let kind = 'custom'
  if (selectedCalendarType.value !== CalendarType.CLASS_BOOKING) {
    if (selectedIsZoomAdded === 'true') {
      kind = 'zoom_conference'
    } else if (selectedIsZoomAdded === 'meet') {
      kind = 'google_conference'
    } else if (selectedIsZoomAdded === 'ms_teams') {
      kind = 'ms_teams_conference'
    } else if (selectedIsZoomAdded === 'phone') {
      kind = 'inbound_call'
    } else if (selectedIsZoomAdded === 'address') {
      kind = 'physical'
    }
  }

  // For conference types (zoom, google meet, ms teams), location should be empty
  // as meeting links are auto-generated
  const isConferenceType = [
    'zoom_conference',
    'google_conference',
    'ms_teams_conference',
  ].includes(kind)
  const locationValue = isConferenceType
    ? ''
    : selectedCustomMeetingLocation || ''

  return {
    userId,
    priority: 0.5,
    isZoomAdded:
      selectedCalendarType.value === CalendarType.CLASS_BOOKING
        ? 'false'
        : selectedIsZoomAdded || 'false',
    zoomOauthId: zoomConnection?.oauthId || '',
    msTeamsOauthId: msTeamsConnection?.oauthId || '',
    meetingLocation: locationValue,
    selected: true,
    locationConfigurations: [
      {
        kind,
        location: locationValue,
        position: 0,
        zoomOauthId:
          kind === 'zoom_conference'
            ? zoomConnection?.oauthId || ''
            : undefined,
        msTeamsOauthId:
          kind === 'ms_teams_conference'
            ? msTeamsConnection?.oauthId || ''
            : undefined,
      },
    ],
  }
}

const updateMeetingLocationRef = (
  value: string,
  teamMemberIndex: number,
  locationConfigIndex: number
) => {
  state.teamMembers[teamMemberIndex].locationConfigurations[
    locationConfigIndex
  ].location =
    state.teamMembers[teamMemberIndex].locationConfigurations[
      locationConfigIndex
    ].location +
    ' ' +
    value
}

const showPriorityDropdown = computed(() => {
  return (
    state.eventType === EventType.RoundRobin_OptimizeForAvailability &&
    selectedCalendarType.value !== CalendarType.CLASS_BOOKING &&
    selectedCalendarType.value !== CalendarType.PERSONAL
  )
})

const isTeamCalendar = computed(() => {
  return (
    selectedCalendarType.value === CalendarType.ROUND_ROBIN ||
    selectedCalendarType.value === CalendarType.CLASS_BOOKING ||
    selectedCalendarType.value === CalendarType.SERVICE ||
    selectedCalendarType.value === CalendarType.PERSONAL ||
    selectedCalendarType.value === CalendarType.COLLECTIVE
  )
})

const teamMemberDropdownTooltip = computed(() => {
  if (
    teamMembersWithFullDetails.value.length === 1 &&
    teamMembersWithFullDetails.value[0].locationConfigurations.length > 1 &&
    selectedCalendarType.value !== CalendarType.PERSONAL
  ) {
    return t('calendar_advanced.meeting_details.only_one_team_member')
  }

  if (selectedCalendarType.value === CalendarType.PERSONAL) {
    return t(
      'calendar_advanced.meeting_details.only_one_team_member_personal_calendar'
    )
  }

  return selectedCalendarType.value === CalendarType.CLASS_BOOKING
    ? t(
        'calendar_advanced.meeting_details.team_member_disabled_class_booking_tooltip'
      )
    : t(
        'calendar_advanced.meeting_details.team_member_disabled_recurring_tooltip'
      )
})

const commonStyles = {
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
}

const renderLabel = (option, selected) => {
  return h(
    'div',
    {
      style: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        marginTop:
          option.type === 'group' && option.key === 'disabled-options'
            ? '5px'
            : '0',
      },
    },
    [
      option.type === 'group' && option.key === 'disabled-options'
        ? h('div', {
            style: {
              height: '1px',
              backgroundColor: 'var(--gray-300)',
              marginBottom: '8px',
              width: '100%',
              flexShrink: 0,
              alignSelf: 'stretch',
            },
          })
        : null,
      h(
        'div',
        {
          style: {
            ...commonStyles,
            width: '100%',
          },
          class: selected ? 'bg-primary' : '',
        },
        [
          h(
            'p',
            {
              style: {
                margin: 0,
                width: '100%',
              },
            },
            [option.label as string]
          ),
          option.description &&
            h(
              'span',
              {
                style: {
                  color: 'var(--gray-500)',
                },
              },
              option.description
            ),
        ]
      ),
    ]
  )
}
const renderTeamMemberOption = ({ node, option }) => {
  if (option.disabled && option.tooltip) {
    return h(
      UITooltip,
      { placement: 'top', style: { width: '100%' } },
      {
        trigger: () => h('div', { style: { width: '100%' } }, [node]),
        default: () => option.tooltip,
      }
    )
  }
  return node
}
</script>

<template>
  <UIForm
    id="create-calendar-form"
    ref="formRef"
    :model="state.formData"
    :rules="rules"
  >
    <UIFormItem
      v-if="selectedCalendarType === CalendarType.ROUND_ROBIN"
      :label="$t('calendar_advanced.meeting_details.meeting_distribution')"
      :tooltip="
        getTooltip(
          $t('calendar_advanced.meeting_details.meeting_distribution_tooltip')
        )
      "
      :show-feedback="false"
    >
      <div class="flex w-full flex-col space-y-4">
        <div class="flex items-center space-x-4">
          <UIRadio
            id="calendar-opt-avail"
            :checked="
              state.eventType === EventType.RoundRobin_OptimizeForAvailability
            "
            :value="EventType.RoundRobin_OptimizeForAvailability"
            :name="EventType.RoundRobin_OptimizeForAvailability"
            @change="
              state.eventType = EventType.RoundRobin_OptimizeForAvailability
            "
          >
            <div class="flex flex-row items-center">
              <div class="text-sm font-medium text-gray-700">
                {{ $t('calendar_advanced.meeting_details.opt_avail') }}
              </div>
              <Tooltip
                :message="
                  $t('calendar_advanced.meeting_details.opt_avail_tooltip')
                "
              />
            </div>
          </UIRadio>

          <UIRadio
            id="calendar-opt-distr"
            :checked="
              state.eventType ===
              EventType.RoundRobin_OptimizeForEqualDistribution
            "
            :value="EventType.RoundRobin_OptimizeForEqualDistribution"
            :name="EventType.RoundRobin_OptimizeForEqualDistribution"
            @change="
              state.eventType =
                EventType.RoundRobin_OptimizeForEqualDistribution
            "
          >
            <div class="flex flex-row items-center">
              <div class="text-sm font-medium text-gray-700">
                {{ $t('calendar_advanced.meeting_details.opt_equal') }}
              </div>
              <Tooltip
                :message="
                  $t('calendar_advanced.meeting_details.opt_equal_tooltip')
                "
              />
            </div>
          </UIRadio>
        </div>
      </div>
    </UIFormItem>

    <UIFormItem v-if="isTeamCalendar" path="teamMembers">
      <div class="flex w-full flex-col">
        <div class="mb-2 flex flex-row justify-between">
          <div class="flex items-center">
            {{
              selectedCalendarType === CalendarType.ROUND_ROBIN
                ? $t('calendar_advanced.meeting_details.team_members_location')
                : $t('calendar_advanced.meeting_details.team_member_location')
            }}
            <Tooltip
              :message="
                $t(
                  'calendar_advanced.meeting_details.team_members_location_tooltip'
                )
              "
            />
          </div>
          <div
            v-if="selectedCalendarType === CalendarType.ROUND_ROBIN"
            class="flex cursor-pointer items-center gap-2 self-end rounded-lg px-[12px] py-[6px]"
          >
            <Settings01Icon
              class="mt-[1px] h-4 w-4 cursor-pointer text-sm font-medium text-primary-700"
              @click="toggleAdvancedSettingsModal"
            />
            <div class="flex flex-row items-center">
              <p
                class="cursor-pointer text-sm font-medium text-primary-700"
                :placement="'top'"
                role="button"
                @click="toggleAdvancedSettingsModal"
                @keypress="toggleAdvancedSettingsModal"
                tabindex="0"
              >
                {{
                  $t(
                    'calendar_advanced.meeting_details.contact_routing_preference'
                  )
                }}
              </p>
              <UITooltip
                :style="{ width: '300px' }"
                class="cursor-default"
                placement="top"
              >
                <template #trigger>
                  <InfoCircleIcon class="ml-2 h-4 w-4 text-primary-700" />
                </template>
                {{
                  $t(
                    'calendar_advanced.meeting_details.contact_routing_preference_tooltip',
                    {
                      contactsLabel:
                        AppState.customObjectLabels?.contact?.plural?.toLowerCase(),
                    }
                  )
                }}
              </UITooltip>
            </div>
          </div>
        </div>
        <UITooltip
          v-if="
            (AppState.editCalendarValidationData.enableRecurring &&
              state.teamMembers.length === 1) ||
            selectedCalendarType === CalendarType.CLASS_BOOKING ||
            selectedCalendarType === CalendarType.PERSONAL ||
            (teamMembersWithFullDetails.length === 1 &&
              teamMembersWithFullDetails[0].locationConfigurations.length > 1)
          "
          :placement="'top'"
        >
          <template #trigger>
            <UISelect
              id="calendar-team-member-select"
              :value="teamMembersWithFullDetails.map(x => x.userId)"
              :multiple="true"
              :filterable="true"
              :options="usersInLocationOptions"
              :render-option="renderTeamMemberOption"
              @change="methods.teamMembersChanged"
              :placeholder="t('create_calendar.service_select_team_member')"
            />
          </template>
          <p
            :class="{
              'max-w-sm':
                teamMembersWithFullDetails.length === 1 &&
                teamMembersWithFullDetails[0].locationConfigurations.length > 1,
            }"
          >
            {{ teamMemberDropdownTooltip }}
          </p>
        </UITooltip>
        <UISelect
          v-else
          id="calendar-team-member-select"
          :value="teamMembersWithFullDetails.map(x => x.userId)"
          :multiple="true"
          :filterable="true"
          :options="usersInLocationOptions"
          :render-option="renderTeamMemberOption"
          @change="methods.teamMembersChanged"
          :placeholder="t('create_calendar.service_select_team_member')"
        />
      </div>
    </UIFormItem>
  </UIForm>
  <div class="w-full">
    <div
      v-for="(teamMember, ind) in teamMembersWithFullDetails"
      :key="ind"
      class="flex w-full items-start justify-between py-3"
    >
      <div class="flex w-[35%] items-center space-x-3">
        <div v-if="teamMember.profilePhoto" class="flex items-center space-x-3">
          <img
            :src="teamMember.profilePhoto"
            class="h-8 w-8 rounded-full object-center"
            :alt="$t('calendar_advanced.meeting_details.profile_photo_alt')"
          />
        </div>
        <UIAvatar v-else size="medium" objectFit="fill" round="true">
          {{ getInitials(teamMember?.name) }}
        </UIAvatar>

        <div :id="teamMember?.userId" name="team-Member-name" class="w-[90%]">
          {{ teamMember.name }}
        </div>
      </div>

      <div
        v-if="
          selectedCalendarType === CalendarType.ROUND_ROBIN ||
          selectedCalendarType === CalendarType.CLASS_BOOKING ||
          selectedCalendarType === CalendarType.SERVICE ||
          selectedCalendarType === CalendarType.PERSONAL
        "
        class="w-[65%]"
      >
        <div
          v-for="(locationConfig, index) in teamMember.locationConfigurations"
          :key="index"
          class="flex w-full flex-col"
        >
          <div class="flex w-full items-start space-x-4">
            <div
              v-if="showPriorityDropdown && index === 0"
              class="flex w-[50%] !flex-col"
            >
              <div class="mb-[8px] flex flex-row items-center">
                <div class="text-[14px] font-medium text-gray-700">
                  {{ t('calendar_advanced.meeting_details.priority') }}
                </div>
                <Tooltip
                  :message="
                    $t('calendar_advanced.meeting_details.priority_tooltip')
                  "
                />
              </div>
              <UISelect
                :id="`${teamMember?.userId}-calendar-team-member-priority`"
                :value="teamMember.priority"
                :options="priorityOptions"
                placeholder=""
                class="w-full"
                @change="
                  value => methods.priorityChanged(teamMember.userId, value)
                "
              />
            </div>

            <div class="flex w-full !flex-col">
              <div
                v-if="index === 0"
                class="flex w-full flex-row items-center gap-2 mb-[8px]"
              >
                <div class="text-[14px] font-medium text-gray-700">
                  {{ t('calendar_advanced.meeting_details.meeting_location_label') }}
                </div>
                <UITooltip>
                  <template #trigger>
                    <InfoCircleIcon class="h-4 w-4 text-gray-500" />
                  </template>
                  <p>
                    {{
                      t(
                        'calendar_advanced.meeting_details.meeting_location_tooltip'
                      )
                    }}
                  </p>
                </UITooltip>
              </div>
              <div class="w-full self-end flex flex-col">
                <UISelect
                  :id="`${teamMember?.userId}-calendar-team-member-meeting-location-select`"
                  :value="locationConfig.kind"
                  :class="{
                    ' w-full':
                      (showPriorityDropdown && index === 0) ||
                      state.eventType !==
                        EventType.RoundRobin_OptimizeForAvailability,
                    'w-[66%] self-end pl-[10px]':
                      index !== 0 &&
                      selectedCalendarType !== CalendarType.PERSONAL &&
                      selectedCalendarType !== CalendarType.SERVICE &&
                      state.eventType ===
                        EventType.RoundRobin_OptimizeForAvailability,
                  }"
                  :options="meetingLocationOptions"
                  :render-label="renderLabel"
                  placeholder=""
                  @change="
                    value =>
                      methods.meetingLocationTypeChangeNew(
                        teamMember.userId,
                        value,
                        index
                      )
                  "
                />
                <div v-if="locationConfig.kind === 'booker'" class="flex self-end"
                :class="{
                  'w-[64%]': selectedCalendarType === CalendarType.ROUND_ROBIN && index !== 0,
                  'w-full': selectedCalendarType !== CalendarType.ROUND_ROBIN || index === 0,
                }"
                >
                  <div class="text-sm text-gray-400 mt-[10px]">
                    {{
                      $t(
                        'calendar_advanced.meeting_location.ask_the_booker_desc'
                      )
                    }}
                  </div>
                </div>
              </div>
            </div>

            <UIButton
              :id="`${teamMember?.userId}-team-member-delete`"
              class="h-10 w-10 rounded-lg bg-gray-100 text-black"
              :class="{
                'mt-[30px]': index === 0 && locationConfig.kind === 'booker' ,
                'mt-[26px]': index === 0 && locationConfig.kind !== 'booker',
              }"
              :text="true"
              size="medium"
              @click="
                () => {
                  methods.closeAliasField(teamMember?.userId + index)
                  if (
                    index === 0 &&
                    teamMember?.locationConfigurations?.length === 1
                  ) {
                    methods.removeTeamMember(teamMember.userId)
                  } else {
                    teamMember.locationConfigurations.splice(index, 1)
                  }
                }
              "
            >
              <Trash03Icon class="w-4" />
            </UIButton>
          </div>

          <div
            v-if="methods.shouldAddressEnable(locationConfig)"
            class="space-y-1"
          >
            <UIFormItem path="meetingLocation" label="">
              <!-- Empty div for round robin optimize for availability -->
              <div
                v-if="
                  selectedCalendarType === CalendarType.ROUND_ROBIN &&
                  state.eventType ===
                    EventType.RoundRobin_OptimizeForAvailability
                "
                class="w-[48%]"
              />
              <div class="flex w-full flex-col gap-3">
                <div class="w-full self-end flex flex-col">
                  <UIInput
                    v-if="locationConfig.kind !== 'custom'"
                    :id="`${teamMember?.userId}-calendar-team-member-meeting-location-input`"
                    class="-mt-10 w-64"
                    :model-value="
                      teamMember.locationConfigurations[index].location
                    "
                    :placeholder="$t('placeholders.meeting_location')"
                    @update:model-value="
                      (location: string) =>
                        methods.meetingLocationChangedNew(
                          teamMember.userId,
                          location,
                          index,
                          locationConfig.kind
                        )
                    "
                  />

                  <div v-if="locationConfig.kind === 'custom'" class="flex w-full flex-col gap-1">
                    <UIInputGroup class="-mt-10 h-full w-64">
                      <UIInput
                        :model-value="
                          teamMember.locationConfigurations[index].location
                        "
                        :placeholder="$t('placeholders.meeting_location')"
                        class="h-full flex-1"
                        @update:model-value="
                          (location: string) =>
                            methods.meetingLocationChangedNew(
                              teamMember.userId,
                              location,
                              index,
                              locationConfig.kind
                            )
                        "
                      >
                        <template #suffix>
                          <CustomVariablePickerV2
                            :dropdown-options="meetingLocationRef"
                            :button-class="'absolute bottom-0 right-4 cursor-pointer'"
                            @update:selected-value="
                              val => updateMeetingLocationRef(val, ind, index)
                            "
                          />
                        </template>
                      </UIInput>

                      <UIButton
                        v-if="
                          (selectedCalendarType === CalendarType.ROUND_ROBIN ||
                            selectedCalendarType === CalendarType.PERSONAL) &&
                          state.teamMembers.length === 1
                        "
                        class="h-10"
                        :disabled="
                          isAliasFieldEnabled.includes(teamMember.userId) ||
                          teamMember.locationConfigurations[index].alias?.length
                        "
                      >
                        <div class="flex items-center gap-2">
                          <span
                            class="text-primary-600"
                            @click="
                              () => {
                                if (
                                  !(
                                    isAliasFieldEnabled.includes(
                                      teamMember.userId
                                    ) ||
                                    teamMember.locationConfigurations[index]
                                      .alias?.length
                                  )
                                ) {
                                  methods.openAliasField(
                                    teamMember.userId + index
                                  )
                                }
                              }
                            "
                          >
                            {{
                              $t(
                                'calendar_advanced.meeting_details.add_display_label'
                              )
                            }}
                          </span>
                          <UITooltip>
                            <template #trigger>
                              <InfoCircleIcon
                                class="h-4 w-4 text-primary-600"
                              />
                            </template>
                            <p class="max-w-[350px]">
                              {{
                                $t(
                                  'calendar_advanced.meeting_details.add_display_label_tooltip'
                                )
                              }}
                            </p>
                          </UITooltip>
                        </div>
                      </UIButton>
                    </UIInputGroup>
                  </div>
                  <p
                    v-if="meetingLocationError"
                    class="mt-2 text-[14px] text-red-500"
                  >
                    {{ meetingLocationError }}
                  </p>
                </div>
                <div
                  v-if="
                    teamMember.locationConfigurations[index].alias ||
                    isAliasFieldEnabled.includes(teamMember.userId + index)
                  "
                  class="flex w-full gap-3 self-end"
                >
                  <UIInput
                    :id="`${teamMember?.userId}-calendar-team-member-meeting-alias-input`"
                    class="w-full"
                    :model-value="
                      teamMember.locationConfigurations[index].alias
                    "
                    :placeholder="
                      $t(
                        'calendar_advanced.meeting_details.enter_display_label'
                      )
                    "
                    @update:model-value="
                      (alias: string) =>
                        methods.meetingAliasChanged(
                          teamMember.userId,
                          alias,
                          index
                        )
                    "
                  />
                  <UIButton
                    id="remove-location"
                    class="h-10 w-10 rounded-lg bg-gray-100 text-black"
                    :text="true"
                    aria-labelledby="remove-location"
                    @click="
                      methods.closeAliasField(teamMember.userId + index),
                        (teamMember.locationConfigurations[index].alias = '')
                    "
                  >
                    <Trash03Icon class="w-4" />
                  </UIButton>
                </div>
              </div>
            </UIFormItem>
          </div>
          <hr
            :class="
              methods.shouldAddressEnable(locationConfig)
                ? '-mt-2 mb-4'
                : 'mb-4 mt-4'
            "
          />
          <div
            v-if="
              teamMembersWithFullDetails.length === 1 &&
              index === teamMember.locationConfigurations.length - 1 &&
              (selectedCalendarType === CalendarType.ROUND_ROBIN ||
                selectedCalendarType === CalendarType.SERVICE ||
                selectedCalendarType === CalendarType.PERSONAL)
            "
            class="flex w-full items-center justify-end gap-2"
          >
            <UIButton
              :id="'add-location-btn'"
              :type="'tertiary'"
              :size="'medium'"
              :loading="false"
              :ghost="false"
              :quaternary="false"
              :circle="false"
              :text="true"
              :link-gray="undefined"
              :style="{ backgroundColor: '#EFF4FF' }"
              class="self self-end rounded px-4 py-3"
              @click="
                () =>
                  teamMember.locationConfigurations.push({
                    kind: 'custom',
                    location: '',
                  })
              "
            >
              {{ t('calendar_advanced.meeting_details.add_location') }}
            </UIButton>
            <UITooltip>
              <template #trigger>
                <InfoCircleIcon class="h-4 w-4 text-primary-600" />
              </template>
              <p class="max-w-[350px]">
                {{
                  t('calendar_advanced.meeting_details.multiple_locations_neo')
                }}
              </p>
            </UITooltip>
          </div>
        </div>
      </div>

      <!----------------------- COLLECTIVE CALENDAR ---------------------------->
      <div
        v-else-if="selectedCalendarType === CalendarType.COLLECTIVE"
        class="w-[60%]"
      >
        <div
          v-for="(locationConfig, index) in teamMember.locationConfigurations"
          :key="index"
          class="flex w-full flex-col gap-2"
        >
          <div class="flex w-full flex-row items-center gap-2">
            <div class="text-[14px] font-medium text-gray-700">
              {{ t('calendar_advanced.meeting_details.meeting_location_label') }}
            </div>
            <UITooltip>
              <template #trigger>
                <InfoCircleIcon class="h-4 w-4 text-gray-500" />
              </template>
              <p>
                {{
                  t(
                    'calendar_advanced.meeting_details.meeting_location_tooltip'
                  )
                }}
              </p>
            </UITooltip>
          </div>
          <div class="flex">
            <UISelect
              :id="`${teamMember?.userId}-calendar-team-member-meeting-location-select`"
              :value="locationConfig.kind"
              class="w-full"
              :class="{ '!mr-6': teamMember.isPrimary }"
              :options="meetingLocationOptions"
              placeholder=""
              :render-label="renderLabel"
              @change="
                value =>
                  methods.meetingLocationTypeChangeNew(
                    teamMember.userId,
                    value,
                    index
                  )
              "
            />
            <div
              v-if="teamMember.isPrimary"
              id="`${teamMember?.userId}-team-member-delete`"
            >
              <UITooltip>
                <template #trigger>
                  <div
                    class="flex items-center gap-2 rounded-full bg-gray-200 px-3 py-1.5 font-medium text-black"
                    :text="true"
                  >
                    <UserCircleIcon class="h-4 w-4" />
                    <div class="font-medium text-gray-700">
                      {{ $t('calendar_advanced.meeting_details.owner') }}
                    </div>
                  </div>
                </template>
                <p>
                  {{
                    $t(
                      'calendar_advanced.meeting_details.team_members_location_owner_tooltip'
                    )
                  }}
                </p>
              </UITooltip>
            </div>
            <div v-else class="flex items-center gap-3">
              <UIButton
                id="`${teamMember?.userId}-team-member-delete`"
                class="ml-4 text-gray-500"
                :text="true"
                @click="() => methods.isPrimaryChanged(teamMember.userId, true)"
              >
                {{ t('calendar_advanced.meeting_details.make_owner') }}
              </UIButton>

              <UIButton
                id="`${teamMember?.userId}-team-member-delete`"
                class="text-red-600"
                :class="{
                  invisible: teamMember.isPrimary,
                }"
                :text="true"
                aria-labelledby="remove-location"
                @click="() => methods.removeTeamMember(teamMember.userId)"
              >
                <Trash03Icon class="w-4" />
              </UIButton>
            </div>
          </div>
          <div v-if="locationConfig.kind === 'custom'" class="space-y-1">
            <UIFormItem path="meetingLocation" label="" :show-feedback="false">
              <UIInput
                v-if="methods.shouldAddressEnable(locationConfig)"
                :id="`${teamMember?.userId}-calendar-team-member-meeting-location-input`"
                class="-mt-10 w-64"
                :model-value="locationConfig.location"
                :placeholder="
                  $t(
                    'calendar_advanced.meeting_details.meeting_location_placeholder'
                  )
                "
                @update:model-value="
                  value =>
                    methods.meetingLocationChanged(teamMember.userId, value)
                "
              >
                <template #suffix>
                  <CustomVariablePickerV2
                    :dropdown-options="meetingLocationRef"
                    :button-class="'absolute bottom-0 right-4 cursor-pointer'"
                    @update:selected-value="
                      val => updateMeetingLocationRef(val, ind, 0)
                    "
                  />
                </template>
              </UIInput>
            </UIFormItem>
          </div>
        </div>
      </div>
    </div>
  </div>
  <AdvancedCalendarScheduleSettingsModal
    :show="state.showAdvancedSettings"
    :close-modal="toggleAdvancedSettingsModal"
    @on-advanced-scheduling-settings-update="methods.emitAdvancedFormData"
  />
</template>
